import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { cn } from '@jeraisonne/ui';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import { OfferToChildCard } from '@/components/offer-to-child-card';
import { isIOSNative, isNativeApp } from '@/lib/platform';
import { PaywallEmailCTA } from '@/components/paywall-email-cta';
import { PurchaseButton } from '@/components/purchase-button';
import type { SchoolLevel } from '@jeraisonne/db';
import { markCheckoutStarted, passCheckoutMark } from '@/lib/analytics/track';
import {
  PassDurationGrid,
  passCheckoutLabel,
  usePassChoice,
} from '@/components/pass-duration-grid';

const EPREUVE_LEVELS: SchoolLevel[] = ['3e', '1re', 'tle'];

type PassDuration = '24h' | '7d' | '30d';

const PASS_PRICES: Record<'normal' | 'epreuves', Record<PassDuration, string>> = {
  normal: { '24h': '1,99 €', '7d': '3,99 €', '30d': '9,99 €' },
  epreuves: { '24h': '2,99 €', '7d': '5,99 €', '30d': '15,99 €' },
};

const PLAN_LABELS: Record<string, string> = {
  student_monthly: 'Abonnement élève mensuel',
  family_monthly: 'Abonnement famille mensuel',
  tutor_monthly: 'Abonnement tuteur mensuel',
};

type ActiveSubscription = {
  id: string;
  plan: string;
  status: string;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  /**
   * true si l'élève est **titulaire d'un siège** (sub famille d'un parent),
   * false si l'élève est propriétaire de la sub (sub Élève qu'il a payée
   * lui-même). Cache le bouton « Gérer / résilier » (impossible depuis un
   * siège) et remplace le libellé.
   */
  via_seat: boolean;
};

type ActivePass = {
  id: string;
  type: string;
  expires_at: string;
  stripe_subscription_id: string | null;
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function formatCountdown(expiresAt: string): string {
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0) return 'Expiré';
  const h = Math.floor(diff / 3_600_000);
  const d = Math.floor(diff / 86_400_000);
  if (d >= 1) return `${d} jour${d > 1 ? 's' : ''}`;
  if (h >= 1) return `${h} heure${h > 1 ? 's' : ''}`;
  const m = Math.floor((diff % 3_600_000) / 60_000);
  return `${m} min`;
}

async function fetchActiveSubscription(userId: string): Promise<ActiveSubscription | null> {
  // 1) Sub dont l'élève est PROPRIÉTAIRE (achat direct par l'élève).
  const ownerP = supabase
    .from('subscriptions')
    .select('id, plan, status, current_period_end, cancel_at_period_end')
    .eq('owner_id', userId)
    .in('status', ['active', 'trialing'])
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  // 2) Sub dont l'élève est TITULAIRE D'UN SIÈGE (sub Famille achetée par un
  //    parent et attribuée à cet enfant). RLS `subscription_seats: own_select`
  //    + `subscriptions: seat_holder_select` (0007) autorisent l'accès en
  //    lecture. Sans ce chemin, un enfant en siège voyait « Aucun accès actif »
  //    alors que `has_active_subscription` (owner OU seat) débloque bien
  //    ses exercices — écran incohérent, et sur iOS le CTA d'achat s'affichait
  //    → risque de DOUBLE PAIEMENT.
  const seatP = supabase
    .from('subscription_seats')
    .select(
      'subscription:subscriptions!inner(id, plan, status, current_period_end, cancel_at_period_end)',
    )
    .eq('user_id', userId)
    .in('subscription.status', ['active', 'trialing'])
    .limit(1)
    .maybeSingle();

  const [{ data: owned }, { data: seated }] = await Promise.all([ownerP, seatP]);
  if (owned) return { ...(owned as Omit<ActiveSubscription, 'via_seat'>), via_seat: false };
  const seatedSub = (seated as { subscription?: Omit<ActiveSubscription, 'via_seat'> } | null)
    ?.subscription;
  if (seatedSub) return { ...seatedSub, via_seat: true };
  return null;
}

async function fetchActivePasses(userId: string): Promise<ActivePass[]> {
  const { data } = await supabase
    .from('access_rights')
    .select('id, type, expires_at, stripe_subscription_id')
    .eq('user_id', userId)
    .gt('expires_at', new Date().toISOString())
    .order('expires_at', { ascending: false });
  return (data ?? []) as ActivePass[];
}

async function fetchStudentLevel(userId: string): Promise<SchoolLevel | null> {
  const { data } = await supabase
    .from('student_profiles')
    .select('level')
    .eq('id', userId)
    .maybeSingle();
  return (data?.level as SchoolLevel) ?? null;
}

/** Études supérieures (profil « Étudiant », migration 0237) : un élève, sans classe. */
async function fetchIsHigherEd(userId: string): Promise<boolean> {
  const { data } = await supabase
    .from('student_profiles')
    .select('is_higher_ed_student')
    .eq('id', userId)
    .maybeSingle();
  return (data as { is_higher_ed_student?: boolean } | null)?.is_higher_ed_student === true;
}

/**
 * « Offrir cet accès à mon enfant » : seulement sur le compte d'un ACHETEUR (compte créé au paiement
 * public, migration 0122 — rôle élève, sans classe), jamais sur celui d'un vrai élève (une classe ou
 * des études supérieures) : « mon enfant » n'y a aucun sens (retour du 29/10/2026). PUR.
 */
export function canOfferAccessToChild(opts: {
  hasActiveAccess: boolean;
  studentLevel: SchoolLevel | null | undefined;
  isHigherEd: boolean | undefined;
}): boolean {
  return opts.hasActiveAccess && opts.studentLevel == null && opts.isHigherEd === false;
}

function SubscriptionCard({
  sub,
  onManage,
  isManageLoading,
}: {
  sub: ActiveSubscription;
  onManage: () => void;
  isManageLoading: boolean;
}) {
  return (
    <div className="border-xp-200 bg-xp-50 space-y-3 rounded-2xl border p-5">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-xp-600 font-sans text-[11px] font-semibold uppercase tracking-wider">
            Abonnement actif
          </p>
          <h3 className="font-display text-ink-900 mt-0.5 text-base font-extrabold">
            {PLAN_LABELS[sub.plan] ?? sub.plan}
          </h3>
        </div>
        <span className="bg-xp-100 text-xp-700 shrink-0 rounded-full px-2.5 py-0.5 font-sans text-xs font-semibold">
          Actif
        </span>
      </div>

      {sub.current_period_end && (
        <p className="text-ink-500 font-sans text-sm">
          {sub.cancel_at_period_end
            ? `Se termine le ${formatDate(sub.current_period_end)}`
            : `Prochain renouvellement le ${formatDate(sub.current_period_end)}`}
        </p>
      )}

      {sub.cancel_at_period_end && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2">
          <p className="font-sans text-xs font-semibold text-amber-800">
            Résiliation programmée — l'accès reste actif jusqu'à la fin de la période.
          </p>
        </div>
      )}

      {sub.via_seat ? (
        <p className="text-ink-500 rounded-xl bg-white/60 px-3 py-2 font-sans text-xs leading-relaxed">
          Abonnement offert par un parent. La gestion / résiliation se fait depuis son compte.
        </p>
      ) : (
        <button
          onClick={onManage}
          disabled={isManageLoading}
          className="border-xp-300 text-xp-700 min-h-[2.75rem] w-full rounded-xl border py-2.5 font-sans text-sm font-semibold transition-all active:scale-[.98] disabled:opacity-60"
        >
          {isManageLoading ? 'Redirection…' : 'Gérer ou résilier mon abonnement'}
        </button>
      )}
    </div>
  );
}

function PassCard({
  pass,
  onManage,
  isManageLoading,
}: {
  pass: ActivePass;
  onManage: () => void;
  isManageLoading: boolean;
}) {
  const [countdown, setCountdown] = useState(formatCountdown(pass.expires_at));

  useEffect(() => {
    const id = setInterval(() => setCountdown(formatCountdown(pass.expires_at)), 60_000);
    return () => clearInterval(id);
  }, [pass.expires_at]);

  const isRecurring = !!pass.stripe_subscription_id;
  const typeLabel = pass.type === 'epreuves' ? 'Passe épreuves' : 'Passe exercices';

  return (
    <div className="border-primary-200 bg-primary-50 space-y-3 rounded-2xl border p-5">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-primary-600 font-sans text-[11px] font-semibold uppercase tracking-wider">
            Passe actif
          </p>
          <h3 className="font-display text-ink-900 mt-0.5 text-base font-extrabold">{typeLabel}</h3>
        </div>
        <span className="bg-primary-100 text-primary-700 shrink-0 rounded-full px-2.5 py-0.5 font-sans text-xs font-semibold">
          {countdown} restant{countdown.includes('min') ? '' : 's'}
        </span>
      </div>

      <p className="text-ink-500 font-sans text-sm">
        Expire le {formatDate(pass.expires_at)}
        {isRecurring ? ' · se renouvelle automatiquement' : ''}
      </p>

      {isRecurring && (
        <button
          onClick={onManage}
          disabled={isManageLoading}
          className="border-primary-300 text-primary-700 min-h-[2.75rem] w-full rounded-xl border py-2.5 font-sans text-sm font-semibold transition-all active:scale-[.98] disabled:opacity-60"
        >
          {isManageLoading ? 'Redirection…' : 'Gérer ou résilier ce passe'}
        </button>
      )}
    </div>
  );
}

function PurchaseBlock({
  hasActiveAccess,
  studentLevel,
}: {
  hasActiveAccess: boolean;
  studentLevel: SchoolLevel | null;
}) {
  const [expanded, setExpanded] = useState(!hasActiveAccess);
  const {
    settings: offerSettings,
    duration: selectedDuration,
    setDuration: setSelectedDuration,
  } = usePassChoice('7d');
  const [selectedType, setSelectedType] = useState<'normal' | 'epreuves'>('normal');
  const [isLoading, setIsLoading] = useState(false);
  const [isRentreeLoading, setIsRentreeLoading] = useState(false);
  const [rentreeError, setRentreeError] = useState<string | null>(null);
  const [isParentLinkLoading, setIsParentLinkLoading] = useState(false);
  const [parentLinkUrl, setParentLinkUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const showEpreuves = studentLevel !== null && EPREUVE_LEVELS.includes(studentLevel);
  // L'offre rentrée est réservée aux nouveaux clients ; le serveur revérifie.
  // On l'affiche donc seulement quand l'élève n'a aucun accès actif.
  const showRentreePromo = !hasActiveAccess;

  async function handleCheckout() {
    setIsLoading(true);
    markCheckoutStarted(passCheckoutMark(selectedType, selectedDuration));
    const { data, error } = await supabase.functions.invoke('create-pass-checkout', {
      body: { type: selectedType, duration: selectedDuration, return_to: window.location.href },
    });
    if (error || !data?.url) {
      setIsLoading(false);
      return;
    }
    window.location.href = data.url as string;
  }

  async function handleRentreeCheckout() {
    setIsRentreeLoading(true);
    setRentreeError(null);
    markCheckoutStarted('abonnement-mois');
    const { data, error } = await supabase.functions.invoke('create-pass-checkout', {
      body: { type: 'normal', duration: '30d', promo: 'rentree', return_to: window.location.href },
    });
    if (error || !data?.url) {
      setIsRentreeLoading(false);
      setRentreeError('Offre réservée aux nouveaux abonnés.');
      return;
    }
    window.location.href = data.url as string;
  }

  async function handleParentLink() {
    setIsParentLinkLoading(true);
    const { data, error } = await supabase.functions.invoke('create-parent-link', {
      body: { offer_type: selectedType, duration: selectedDuration },
    });
    setIsParentLinkLoading(false);
    if (error) return;
    setParentLinkUrl((data as { url: string }).url);
  }

  async function handleCopy() {
    if (!parentLinkUrl) return;
    await navigator.clipboard.writeText(parentLinkUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function handleShare() {
    if (!parentLinkUrl || !navigator.share) return;
    await navigator.share({
      title: 'Paiement Jeraisonne',
      text: 'Clique ici pour payer mon accès Jeraisonne',
      url: parentLinkUrl,
    });
  }

  if (!expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className="border-primary-200 text-primary-700 min-h-[2.75rem] w-full rounded-2xl border-2 py-3 font-sans text-sm font-semibold transition-all active:scale-[.98]"
      >
        + Ajouter / prolonger un accès
      </button>
    );
  }

  return (
    <div className="space-y-4">
      {showRentreePromo && (
        <div className="space-y-3 rounded-2xl border-2 border-amber-300 bg-gradient-to-b from-amber-50 to-white p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <span className="rounded-full bg-amber-500 px-2 py-0.5 font-sans text-[10px] font-semibold uppercase tracking-wide text-white">
                Offre limitée
              </span>
              <h3 className="font-display text-ink-900 mt-1.5 text-lg font-extrabold">
                Spéciale Rentrée
              </h3>
              <p className="text-ink-500 font-sans text-xs">Pack exercices (hors épreuves)</p>
            </div>
            <div className="text-right">
              <p className="font-display text-ink-900 text-2xl font-extrabold">4,99 €</p>
              <p className="text-ink-400 font-sans text-[11px]">/ mois × 3 mois</p>
            </div>
          </div>
          <p className="text-ink-600 font-sans text-xs leading-relaxed">
            4,99 €/mois les 3 premiers mois, puis 9,99 €/mois. Abonnement résiliable à tout moment.
            Valable jusqu'au 31 décembre.
          </p>
          <button
            onClick={handleRentreeCheckout}
            disabled={isRentreeLoading}
            className="font-display min-h-[2.75rem] w-full rounded-xl bg-amber-500 py-3.5 text-sm font-bold text-white shadow transition-all hover:bg-amber-600 active:scale-[.98] disabled:opacity-60"
          >
            {isRentreeLoading ? 'Redirection…' : 'Profiter de l’offre · 4,99 €'}
          </button>
          {rentreeError && (
            <p className="text-center font-sans text-xs font-semibold text-amber-700">
              {rentreeError}
            </p>
          )}
        </div>
      )}

      <div className="border-primary-200 from-primary-50 to-ink-50 space-y-4 rounded-2xl border-2 bg-gradient-to-b p-5">
        <div className="flex items-start justify-between gap-2">
          <div className="space-y-1">
            <p className="text-primary-500 font-sans text-[11px] font-semibold uppercase tracking-wider">
              Ajouter un accès
            </p>
            <h3 className="font-display text-ink-900 text-xl font-extrabold">
              Prolonger ou étendre mon accès
            </h3>
            <p className="text-ink-500 font-sans text-sm leading-relaxed">
              Pass 24 h sans engagement, ou abonnement 7 j / 30 j résiliable.
            </p>
          </div>
          <button
            onClick={() => setExpanded(false)}
            className="text-ink-400 hover:text-ink-600 mt-0.5 shrink-0 p-1 text-lg leading-none"
            aria-label="Réduire"
          >
            ×
          </button>
        </div>

        <div className={cn('gap-2', showEpreuves ? 'grid grid-cols-2' : 'flex')}>
          <button
            onClick={() => setSelectedType('normal')}
            className={cn(
              'rounded-xl border-2 px-3 py-2.5 text-center transition-all active:scale-[.98]',
              !showEpreuves && 'flex-1',
              selectedType === 'normal'
                ? 'border-primary-400 bg-primary-50'
                : 'border-ink-200 bg-white',
            )}
          >
            <p
              className={cn(
                'font-display text-sm font-bold',
                selectedType === 'normal' ? 'text-primary-700' : 'text-ink-800',
              )}
            >
              Exercices
            </p>
            <p
              className={cn(
                'mt-0.5 font-sans text-[10px]',
                selectedType === 'normal' ? 'text-primary-500' : 'text-ink-400',
              )}
            >
              Tous niveaux
            </p>
          </button>
          {showEpreuves && (
            <button
              onClick={() => setSelectedType('epreuves')}
              className={cn(
                'rounded-xl border-2 px-3 py-2.5 text-center transition-all active:scale-[.98]',
                selectedType === 'epreuves'
                  ? 'border-primary-400 bg-primary-50'
                  : 'border-ink-200 bg-white',
              )}
            >
              <p
                className={cn(
                  'font-display text-sm font-bold',
                  selectedType === 'epreuves' ? 'text-primary-700' : 'text-ink-800',
                )}
              >
                Épreuves
              </p>
              <p
                className={cn(
                  'mt-0.5 font-sans text-[10px]',
                  selectedType === 'epreuves' ? 'text-primary-500' : 'text-ink-400',
                )}
              >
                Officielles
              </p>
            </button>
          )}
        </div>

        <PassDurationGrid
          prices={PASS_PRICES[selectedType]}
          value={selectedDuration}
          onChange={setSelectedDuration}
          settings={offerSettings}
        />

        <button
          onClick={handleCheckout}
          disabled={isLoading}
          className="bg-primary-400 font-display hover:bg-primary-500 min-h-[2.75rem] w-full rounded-xl py-3.5 text-sm font-bold text-white shadow transition-all active:scale-[.98] disabled:opacity-60"
        >
          {isLoading
            ? 'Redirection…'
            : passCheckoutLabel(
                selectedDuration,
                PASS_PRICES[selectedType][selectedDuration],
                offerSettings,
              )}
        </button>

        <div className="flex items-center gap-2">
          <div className="border-ink-200 flex-1 border-t" />
          <span className="text-ink-400 font-sans text-xs">ou</span>
          <div className="border-ink-200 flex-1 border-t" />
        </div>

        <button
          onClick={handleParentLink}
          disabled={isParentLinkLoading || !!parentLinkUrl}
          className="text-primary-600 border-primary-200 min-h-[2.75rem] w-full rounded-xl border py-3 font-sans text-sm font-semibold transition-all active:scale-[.98] disabled:opacity-60"
        >
          {isParentLinkLoading ? 'Génération…' : 'Demander à mes parents'}
        </button>

        {parentLinkUrl && (
          <div className="bg-ink-50 border-ink-200 space-y-3 rounded-xl border p-4">
            <p className="text-ink-700 font-sans text-xs font-semibold">
              Partage ce lien à tes parents — ils peuvent payer sans créer de compte :
            </p>
            <div className="border-ink-200 rounded-lg border bg-white px-3 py-2">
              <p className="text-ink-600 break-all font-mono text-[11px]">{parentLinkUrl}</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleCopy}
                className="border-ink-200 flex-1 rounded-lg border py-2 font-sans text-xs font-semibold transition-all active:scale-[.98]"
              >
                {copied ? 'Copié !' : 'Copier le lien'}
              </button>
              {typeof navigator.share === 'function' && (
                <button
                  onClick={handleShare}
                  className="bg-primary-400 flex-1 rounded-lg py-2 font-sans text-xs font-semibold text-white transition-all active:scale-[.98]"
                >
                  Partager
                </button>
              )}
            </div>
          </div>
        )}

        <p className="text-ink-400 text-center font-sans text-xs">
          Ton professeur peut aussi t'assigner des exercices gratuitement
        </p>
      </div>
    </div>
  );
}

export function StudentSubscriptionBlock() {
  const { user } = useAuth();
  const studentId = user?.id ?? '';
  const qc = useQueryClient();
  const [isManageLoading, setIsManageLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('pass_success') !== '1' || !studentId) return;
    window.history.replaceState({}, '', window.location.pathname);
    qc.invalidateQueries({ queryKey: ['student-subscription'] });
    qc.invalidateQueries({ queryKey: ['student-passes'] });
  }, [studentId, qc]);

  const { data: subscription, isLoading: subLoading } = useQuery({
    queryKey: ['student-subscription', studentId],
    enabled: !!studentId,
    staleTime: 30_000,
    queryFn: () => fetchActiveSubscription(studentId),
  });

  const { data: passes, isLoading: passesLoading } = useQuery({
    queryKey: ['student-passes', studentId],
    enabled: !!studentId,
    staleTime: 30_000,
    queryFn: () => fetchActivePasses(studentId),
  });

  const { data: studentLevel, isLoading: levelLoading } = useQuery({
    queryKey: ['student-level', studentId],
    enabled: !!studentId,
    staleTime: 300_000,
    queryFn: () => fetchStudentLevel(studentId),
  });

  const { data: isHigherEd } = useQuery({
    queryKey: ['student-higher-ed', studentId],
    enabled: !!studentId,
    staleTime: 300_000,
    queryFn: () => fetchIsHigherEd(studentId),
  });

  const isLoading = subLoading || passesLoading || levelLoading;
  const hasActiveAccess = !!subscription || (passes?.length ?? 0) > 0;
  const showOfferToChild = canOfferAccessToChild({ hasActiveAccess, studentLevel, isHigherEd });

  async function handleManage() {
    setIsManageLoading(true);
    const { data, error } = await supabase.functions.invoke('create-portal-session', {
      body: { return_to: window.location.href },
    });
    if (error || !data?.url) {
      setIsManageLoading(false);
      return;
    }
    window.location.href = data.url as string;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="border-primary-400 h-6 w-6 animate-spin rounded-full border-2 border-t-transparent" />
      </div>
    );
  }

  // ── Natif : iOS = IAP (App Store 3.1.1) / Android = Reader app ────────────
  // Dans les deux cas on MASQUE les CTAs Stripe / parent link / portail Stripe
  // et l'`OfferToChildCard` (comporte un CTA achat).
  //   - iOS : Apple impose l'In-App Purchase → on vend l'abonnement mensuel
  //     via `PurchaseButton` (feuille système / RevenueCat). La gestion /
  //     résiliation renvoie vers les Réglages Apple (autorisé, IAP oblige).
  //   - Android : Google Play Billing pas encore configuré côté RC → on reste
  //     sur le CTA email (Reader app) + gestion sur le web.
  if (isNativeApp()) {
    const readOnlyCards = (
      <>
        {subscription && <SubscriptionCardReadOnly sub={subscription} />}
        {passes?.map((pass) => (
          <PassCardReadOnly key={pass.id} pass={pass} />
        ))}
      </>
    );

    if (isIOSNative()) {
      return (
        <div className="space-y-4">
          {readOnlyCards}
          {!hasActiveAccess ? (
            <div className="border-primary-200 from-primary-50 to-ink-50 space-y-4 rounded-2xl border-2 bg-gradient-to-b p-5">
              <div className="space-y-1 text-center">
                <p className="text-primary-500 font-sans text-[11px] font-semibold uppercase tracking-wider">
                  Passer à Jeraisonne
                </p>
                <h3 className="font-display text-ink-900 text-xl font-extrabold">
                  Débloque tous les exercices
                </h3>
                <p className="text-ink-500 font-sans text-sm leading-relaxed">
                  Accès à tous les exercices, résiliable à tout moment.
                </p>
              </div>
              <PurchaseButton
                productId="jeraisonne_student_monthly"
                context={{ source: 'student-subscription-block' }}
              />
              <p className="text-ink-400 text-center font-sans text-xs">
                La première étape de chaque exercice reste toujours gratuite.
              </p>
            </div>
          ) : (
            <NativeManageSubscriptionButton />
          )}
        </div>
      );
    }

    // Android — Reader app (email + gestion web).
    return (
      <div className="space-y-4">
        {readOnlyCards}
        {!hasActiveAccess ? (
          <div className="border-primary-200 from-primary-50 to-ink-50 space-y-4 rounded-2xl border-2 bg-gradient-to-b p-5">
            <div className="space-y-1 text-center">
              <p className="text-primary-500 font-sans text-[11px] font-semibold uppercase tracking-wider">
                Passer à Jeraisonne
              </p>
              <h3 className="font-display text-ink-900 text-xl font-extrabold">
                Débloque tous les exercices
              </h3>
              <p className="text-ink-500 font-sans text-sm leading-relaxed">
                On peut t'envoyer les formules par email pour souscrire à tête reposée.
              </p>
            </div>
            <PaywallEmailCTA context={{ source: 'student-subscription-block' }} />
            <p className="text-ink-400 text-center font-sans text-xs">
              La première étape de chaque exercice reste toujours gratuite.
            </p>
          </div>
        ) : (
          <div className="border-ink-200 rounded-2xl border bg-white p-4 text-center">
            <p className="text-ink-500 font-sans text-xs leading-relaxed">
              Pour gérer ou résilier ton abonnement, rends-toi sur{' '}
              <span className="text-ink-700 font-semibold">jeraisonne.fr</span> depuis un navigateur
              web.
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {subscription && (
        <SubscriptionCard
          sub={subscription}
          onManage={handleManage}
          isManageLoading={isManageLoading}
        />
      )}

      {passes?.map((pass) => (
        <PassCard
          key={pass.id}
          pass={pass}
          onManage={handleManage}
          isManageLoading={isManageLoading}
        />
      ))}

      {showOfferToChild && <OfferToChildCard />}

      {!hasActiveAccess && (
        <div className="border-ink-200 rounded-2xl border bg-white p-5 text-center">
          <p className="text-3xl">🔒</p>
          <h3 className="font-display text-ink-900 mt-2 text-base font-extrabold">
            Aucun accès actif
          </h3>
          <p className="text-ink-500 mt-1 font-sans text-sm">
            La première étape de chaque exercice est toujours gratuite.
          </p>
        </div>
      )}

      <PurchaseBlock hasActiveAccess={hasActiveAccess} studentLevel={studentLevel ?? null} />
    </div>
  );
}

// ─── Gestion de l'abonnement natif (iOS) ─────────────────────────────────────
// Un abonnement acheté en IAP se résilie / se change UNIQUEMENT via les Réglages
// Apple — jamais via le portail Stripe (qui ne connaît pas les subs IAP).
// Apple autorise à renvoyer vers cet écran système (Guideline 3.1.1).
function NativeManageSubscriptionButton() {
  const [loading, setLoading] = useState(false);

  async function handleManage() {
    setLoading(true);
    const { openManageSubscriptionsNative } = await import('@/lib/checkout/native');
    await openManageSubscriptionsNative();
    setLoading(false);
  }

  return (
    <div className="border-ink-200 space-y-3 rounded-2xl border bg-white p-4 text-center">
      <p className="text-ink-500 font-sans text-xs leading-relaxed">
        Gère ou résilie ton abonnement depuis les Réglages de ton appareil.
      </p>
      <button
        onClick={handleManage}
        disabled={loading}
        className="border-ink-300 text-ink-700 min-h-[2.75rem] w-full rounded-xl border py-2.5 font-sans text-sm font-semibold transition-all active:scale-[.98] disabled:opacity-60"
      >
        {loading ? 'Ouverture…' : 'Gérer mes abonnements'}
      </button>
    </div>
  );
}

// ─── Read-only cards pour iOS Reader app ──────────────────────────────────────
// Miroirs sans le bouton « Gérer ou résilier » (lien vers portail Stripe).

function SubscriptionCardReadOnly({ sub }: { sub: ActiveSubscription }) {
  return (
    <div className="border-xp-200 bg-xp-50 space-y-3 rounded-2xl border p-5">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-xp-600 font-sans text-[11px] font-semibold uppercase tracking-wider">
            Abonnement actif
          </p>
          <h3 className="font-display text-ink-900 mt-0.5 text-base font-extrabold">
            {PLAN_LABELS[sub.plan] ?? sub.plan}
          </h3>
        </div>
        <span className="bg-xp-100 text-xp-700 shrink-0 rounded-full px-2.5 py-0.5 font-sans text-xs font-semibold">
          Actif
        </span>
      </div>

      {sub.current_period_end && (
        <p className="text-ink-500 font-sans text-sm">
          {sub.cancel_at_period_end
            ? `Se termine le ${formatDate(sub.current_period_end)}`
            : `Prochain renouvellement le ${formatDate(sub.current_period_end)}`}
        </p>
      )}

      {sub.via_seat && (
        <p className="text-ink-500 rounded-xl bg-white/60 px-3 py-2 font-sans text-xs leading-relaxed">
          Abonnement offert par un parent.
        </p>
      )}

      {sub.cancel_at_period_end && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2">
          <p className="font-sans text-xs font-semibold text-amber-800">
            Résiliation programmée — l'accès reste actif jusqu'à la fin de la période.
          </p>
        </div>
      )}
    </div>
  );
}

function PassCardReadOnly({ pass }: { pass: ActivePass }) {
  const [countdown, setCountdown] = useState(formatCountdown(pass.expires_at));
  useEffect(() => {
    const id = setInterval(() => setCountdown(formatCountdown(pass.expires_at)), 60_000);
    return () => clearInterval(id);
  }, [pass.expires_at]);

  const isRecurring = !!pass.stripe_subscription_id;
  const typeLabel = pass.type === 'epreuves' ? 'Passe épreuves' : 'Passe exercices';

  return (
    <div className="border-primary-200 bg-primary-50 space-y-3 rounded-2xl border p-5">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-primary-600 font-sans text-[11px] font-semibold uppercase tracking-wider">
            Passe actif
          </p>
          <h3 className="font-display text-ink-900 mt-0.5 text-base font-extrabold">{typeLabel}</h3>
        </div>
        <span className="bg-primary-100 text-primary-700 shrink-0 rounded-full px-2.5 py-0.5 font-sans text-xs font-semibold">
          {countdown} restant{countdown.includes('min') ? '' : 's'}
        </span>
      </div>
      <p className="text-ink-500 font-sans text-sm">
        Expire le {formatDate(pass.expires_at)}
        {isRecurring ? ' · se renouvelle automatiquement' : ''}
      </p>
    </div>
  );
}
